import dayjs from 'dayjs'
import * as jose from 'jose'
import { ReactElement } from 'react'
import { messages } from './messages'
import { endpoints } from './endpoints'
import KeycloakService from './keycloakService'
import {
  Doctor,
  Panels,
  DevicesList,
  PatientDetails,
  VisitMedication,
  IpdDiagnosisData,
  IpdSearchOptions,
} from '../interfaces'
import {
  titleCase,
  createApiUrl,
  createFormApiUrl,
  getLocalDateAndTime,
  getCurrentDateAndTime,
} from './utils'
import {
  intent,
  onHold,
  arrived,
  careplan,
  constants,
  allocation,
  IPD_PROJECT_ID,
  OPD_PROJECT_ID,
  LABS_PROJECT_ID,
  JITSI_SECRET_KEY,
  intensivistCommandCenter,
  careplanCreatedSuccessfully,
} from './constants'

export async function decodeJwe(key: string): Promise<string> {
  let jweToken = ''
  const secretKey = new TextEncoder().encode(JITSI_SECRET_KEY)

  try {
    const { plaintext } = await jose.compactDecrypt(key, secretKey)
    jweToken = new TextDecoder().decode(plaintext)
  } catch (error) {
    console.error(error)
  }
  return jweToken
}

export async function fetchJitsiToken(): Promise<string> {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_DMS_API_URL}${import.meta.env.VITE_JITSI_TOKEN_ENDPOINT}`
    )
    if (!response?.ok) {
      throw new Error(`Failed to fetch token, status code: ${response?.status}`)
    }
    const key = await response?.text()
    if (key) {
      const jwtToken = await decodeJwe(key)
      return jwtToken
    }
    throw new Error('Failed to fetch Jitsi token')
  } catch (error) {
    console.error(error)
    return ''
  }
}

export const fetchAlternateDrug = async (
  name: string,
  patientDetails: PatientDetails,
  drugCloseButton: HTMLButtonElement | null
): Promise<{ label: ReactElement; value: string }[]> => {
  try {
    const payload = {
      intent: intent?.getDrugsByComposition,
      state: {
        name: name,
        location: patientDetails?.locationsList?.find(
          (item) => item?.location === patientDetails?.currentLocation
        )?.id,
      },
    }

    const response = await fetch(createApiUrl(OPD_PROJECT_ID), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${KeycloakService.getToken()}`,
        'Content-Type': 'application/json',
        version: 'WORKING',
      },
      body: JSON.stringify(payload),
    })

    if (!response?.ok) {
      throw new Error(`Error: ${response?.status} - ${response?.statusText}`)
    }

    const data = await response?.json()

    if (data?.length > 0) {
      const drugList = data?.map((drug) => ({
        label: (
          <div key={drug?.id}>
            <div>{drug?.name}</div>
            <div>Qty-{drug?.quantity}</div>
            <div className='druglist-composition'>
              Composition:&nbsp;{drug?.composition}
            </div>
          </div>
        ),
        value: drug?.name,
      }))

      if (drugCloseButton) {
        drugCloseButton.click()
      }

      return drugList
    } else {
      return []
    }
  } catch (error) {
    console.error(error)
    return []
  }
}

export const createVisitForPatient = async (
  patientDetails: PatientDetails,
  locationIdForPayload: string,
  INTENSIVIST_DOCTOR_ID: string | undefined
): Promise<string | null> => {
  const payloadForVisitCreation = {
    intent: intent?.createVisitForCC,
    state: {
      patientId: patientDetails?.patient_fhir_id,
      encounterStatus: arrived,
      startTime: getCurrentDateAndTime(),
      doctorId: INTENSIVIST_DOCTOR_ID,
      locationId: locationIdForPayload,
      bedId: patientDetails?.bedId,
      bedNo: patientDetails?.bedNumber,
      admittedDateAndTime: patientDetails?.admissionDate,
    },
  }

  try {
    const response = await fetch(createApiUrl(IPD_PROJECT_ID), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${KeycloakService.getToken()}`,
        'Content-Type': 'application/json',
        version: 'WORKING',
      },
      body: JSON.stringify(payloadForVisitCreation),
    })

    if (!response?.ok) {
      throw new Error(`Error: ${response?.status} - ${response?.statusText}`)
    }

    const data = await response?.json()
    return data?.data?.id || null
  } catch (error) {
    console.error(error)
    return null
  }
}

export const addToStagingTable = async (
  patientDetails: PatientDetails,
  selectedDoctor: Doctor | null,
  newVisitID: string,
  medicinesPayload: any[],
  panels: Panels[],
  labSuggestions: string
): Promise<boolean> => {
  const payloadForStagingTable = {
    intent: intent?.addToStaging,
    state: {
      created_by: KeycloakService.getSub(),
      suggested_doctor_name: selectedDoctor?.name,
      visit_id: newVisitID,
      lab_tests: [],
      medicine_suggestion: medicinesPayload,
      panels: panels,
      patient_id: patientDetails?.patient_fhir_id,
      requested_by: intensivistCommandCenter,
      source: patientDetails?.currentLocation,
      treatment_plan: labSuggestions,
      user_id: KeycloakService.getSub(),
    },
  }

  try {
    const response = await fetch(createApiUrl(IPD_PROJECT_ID), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${KeycloakService.getToken()}`,
        'Content-Type': 'application/json',
        version: 'WORKING',
      },
      body: JSON.stringify(payloadForStagingTable),
    })

    if (!response?.ok) {
      throw new Error(`Error: ${response?.status} - ${response?.statusText}`)
    }
    return response?.status === 200
  } catch (error) {
    console.error(error)
    return false
  }
}

export const createNotificationForCareplan = async (
  newVisitID: string,
  patientDetails: PatientDetails,
  panels: Panels[],
  selectedDoctor: Doctor | null,
  labSuggestions: string,
  medicinesPayload: any[]
): Promise<any | null> => {
  const payloadForNotification = {
    intent: intent?.createNotification,
    state: {
      visit_id: newVisitID,
      notification: {
        messageType: careplan,
        patientId: patientDetails?.patient_uhid,
        patientLocation: patientDetails?.currentLocation,
        patientName: patientDetails?.patient_name,
        panels: panels,
        suggestedDoctor: selectedDoctor?.name,
        treatment_plan: labSuggestions,
        medicine_suggestion: medicinesPayload,
        timestamp: getCurrentDateAndTime(),
        message: careplanCreatedSuccessfully,
      },
      notificationStatus: onHold,
    },
  }

  try {
    const response = await fetch(createApiUrl(IPD_PROJECT_ID), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${KeycloakService.getToken()}`,
        'Content-Type': 'application/json',
        version: 'WORKING',
      },
      body: JSON.stringify(payloadForNotification),
    })

    if (response?.status !== 200) {
      throw new Error('Unable to create notification')
    }

    return await response?.json()
  } catch (error) {
    console.error('Error creating notification:', error)
    return null
  }
}

export const fetchMedicalHistoryData = async (
  patientDetails: PatientDetails
): Promise<any | null> => {
  const payload = {
    intent: intent?.getCarePlans,
    state: {
      patientId: patientDetails?.patient_fhir_id,
    },
  }

  try {
    const response = await fetch(createApiUrl(IPD_PROJECT_ID), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${KeycloakService.getToken()}`,
        'Content-Type': 'application/json',
        version: 'WORKING',
      },
      body: JSON.stringify(payload),
    })

    if (!response?.ok) {
      throw new Error('Failed to fetch medical history')
    }

    const data = await response?.json()
    return data
  } catch (error) {
    console.error(error)
    return null
  }
}

export const fetchDrugsListData = async (
  value: string,
  patientDetails: PatientDetails
): Promise<any | null> => {
  const payload = {
    intent: intent?.getDrugsByName,
    state: {
      name: value,
      location: patientDetails?.locationsList?.find(
        (item) => item?.location === patientDetails?.currentLocation
      )?.id,
    },
  }

  try {
    const response = await fetch(createApiUrl(OPD_PROJECT_ID), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${KeycloakService.getToken()}`,
        'Content-Type': 'application/json',
        version: 'WORKING',
      },
      body: JSON.stringify(payload),
    })

    if (!response?.ok) {
      throw new Error('Failed to fetch drug list')
    }

    const data = await response?.json()
    return data
  } catch (error) {
    console.error('Error fetching drug list:', error)
    return null
  }
}

export const fetchBedDetailsByLocationAndWards = async (
  selectedWardID: string | undefined,
  selectedLocation: string
): Promise<any[]> => {
  const payload = {
    intent: intent.getBedDetailsByLocationAndWards,
    state: { wardId: Number(selectedWardID), locationId: selectedLocation },
  }

  try {
    const response = await fetch(createApiUrl(IPD_PROJECT_ID), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${KeycloakService.getToken()}`,
        'Content-Type': 'application/json',
        version: 'WORKING',
      },
      body: JSON.stringify(payload),
    })

    if (!response?.ok) {
      throw new Error('Error in fetching bed details by location and wards')
    }

    const data = await response?.json()

    const result = data?.ward_details?.flatMap((ward) => {
      return ward?.room_details?.flatMap((room) => {
        return room?.beds
          ?.filter((bed) => bed?.status === allocation?.occupied)
          ?.map((bed) => {
            const status = bed?.bed_patient_assignment_maps?.find(
              (status) => status?.bed_status === allocation?.assigned
            )
            return {
              id: bed?.id,
              bedNumber: bed?.bed_number,
              name: titleCase(bed?.patient_name),
              admission: bed?.admission_date ?? '',
              categoryName: bed?.bed_category?.name ?? '',
              dischargedDate: bed?.discharged_date ?? '',
              bedCategory:
                bed?.bed_category_id === import.meta.env.VITE_CRITICAL_BED_ID
                  ? allocation?.critical
                  : allocation?.nonCritical,
              status: bed?.status,
              roomId: bed?.room_id,
              mrNumber: bed?.patient_id,
              roomName: room?.room_name,
              roomNumber: room?.room_number,
              roomDetail: `${room?.room_name} ${room?.room_number ?? ''}`,
              locationId: room?.location_id,
              wardName: ward?.ward_name,
              floorNumber: ward?.floor_number,
              bedPatientMapId: status?.bed_id,
              bedPatientId: status?.id,
              bedPatientAssignedId: status?.patient_id,
              bedPatientStatus: status?.bed_status,
              bedPatientDischargeDate: status?.date_of_discharge,
              ecgHeartRate: null,
              systolicBp: null,
              diastolicBp: null,
              spo2: null,
              respiratoryRate: null,
            }
          })
      })
    })

    return result
  } catch (err) {
    console.error(err)
    throw new Error('Failed to fetch bed details')
  }
}

export const getIpdDiagnosisData = async (
  selectedId: string
): Promise<IpdDiagnosisData> => {
  const payload = {
    intent: intent?.ipdDiagnosis,
    state: {
      encounterId: selectedId,
    },
    submit: false,
  }

  try {
    const response = await fetch(createFormApiUrl(IPD_PROJECT_ID), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${KeycloakService.getToken()}`,
        'Content-Type': 'application/json',
        version: 'WORKING',
      },
      body: JSON.stringify(payload),
    })
    if (!response?.ok) {
      throw new Error('Failed to fetch IPD diagnosis data')
    }
    const data = await response?.json()

    return {
      provisionalDiagnosis: data?.state?.provisionalDiagnosis || '',
      differentialDiagnosis: data?.state?.differentialDiagnosis || '',
      investigationsDiagnostics: data?.state?.investigationsDiagnostics || '',
      finalDiagnosis: data?.state?.finalDiagnosis || '',
    }
  } catch (error) {
    console.error(error)
    return {
      provisionalDiagnosis: '',
      differentialDiagnosis: '',
      investigationsDiagnostics: '',
      finalDiagnosis: '',
    }
  }
}

export async function fetchDocumentIDForLabOrders(
  id: number | null
): Promise<string | null> {
  const payloadForDocId = {
    intent: intent?.getDocIdForLabOrder,
    state: {
      id: id,
    },
  }

  try {
    const res = await fetch(createApiUrl(LABS_PROJECT_ID), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${KeycloakService.getToken()}`,
        'Content-Type': 'application/json',
        version: 'WORKING',
      },
      body: JSON.stringify(payloadForDocId),
    })
    if (!res?.ok) throw new Error(`Error: ${res?.status} ${res?.statusText}`)
    const data = await res?.json()
    const documentId = data?.lab_orders_by_pk?.document_id ?? null
    return documentId
  } catch (error) {
    console.error(error)
    throw new Error('Failed to fetch document ID')
  }
}

export async function displayDocument(
  documentId: string | null
): Promise<void> {
  if (!documentId) {
    console.error('Invalid document ID')
    return
  }

  try {
    const downloadUrl = `${import.meta.env.VITE_DMS_API_URL}${import.meta.env.VITE_DMS_ENDPOINT}${documentId}`

    const response = await fetch(downloadUrl, {
      method: 'GET',
    })
    if (!response?.ok) {
      throw new Error(`Error: ${response?.status} ${response?.statusText}`)
    }
    const blob = await response?.blob()
    const blobUrl = URL.createObjectURL(blob)

    const iframe = document.createElement('iframe')
    iframe.src = blobUrl
    iframe.width = '100%'
    iframe.height = '100%'
    iframe.style.border = 'none'

    const newWindow = window.open('', '_blank') as Window
    newWindow.document.body.style.margin = '0'
    newWindow.document.body.appendChild(iframe)
    newWindow.document.title = 'Test Results'
  } catch (error) {
    console.error(error)
  }
}

export const getTreatmentPlansDetails = (
  id: string | null | undefined,
  setOpen: (data: boolean) => void,
  setTreatmentDetails: (data: any) => void
): void => {
  const payload = {
    intent: intent?.treatmentPlanForIPD,
    state: {
      encounterId: id,
    },
    submit: false,
  }
  fetch(createFormApiUrl(IPD_PROJECT_ID), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${KeycloakService.getToken()}`,
      'Content-Type': 'application/json',
      version: 'WORKING',
    },
    body: JSON.stringify(payload),
  })
    .then((res) => {
      if (!res?.ok) throw new Error('Failed to fetch treatment details')
      return res?.json()
    })
    .then((data) => {
      setOpen(true)
      setTreatmentDetails(data?.state)
    })
    .catch((err) => console.error(err))
}

export const sendVitalAlertNotification = (
  patientId: string,
  alarmType: string,
  alarmValue: string,
  alarmDescription: string,
  eventPhase: string,
  wardName: string,
  roomNumber: string,
  bedNumber: string
): void => {
  const myHeaders = new Headers()
  myHeaders.append('Authorization', `Bearer ${KeycloakService.getToken()}`)
  myHeaders.append('Content-Type', 'application/json')

  const raw = JSON.stringify({
    patientId: patientId,
    alarmType: alarmType,
    alarmValue: alarmValue?.toString(),
    alarmDescription: alarmDescription,
    timeStamp: getLocalDateAndTime(),
    eventPhase: eventPhase,
    wardName: wardName,
    roomNumber: roomNumber,
    bedNumber: bedNumber,
    deviceType: 'Mindray',
  })

  const requestOptions: RequestInit = {
    method: 'POST',
    headers: myHeaders,
    body: raw,
    redirect: 'follow',
  }

  const apiUrl = `${import.meta.env.VITE_DMS_API_URL}${import.meta.env.VITE_DOCTOR_APP_API_ENDPOINT}`

  fetch(apiUrl, requestOptions)
    .then((res) => {
      if (!res?.ok) throw new Error('Failed to send doctor app notification')
      return res?.json()
    })
    .then((result) => {
      if (!result?.status) {
        console.error(messages?.vitalAlertApiFailure, result?.message)
      }
    })
    .catch((error) => console.error(error))
}

export const sendCareplanNotification = (
  patientName: string,
  fhirId: string,
  patientAge: string | number,
  patientGender: string,
  doctorId: string,
  newVisitID: string
): void => {
  const myHeaders = new Headers()
  myHeaders.append('Authorization', `Bearer ${KeycloakService.getToken()}`)
  myHeaders.append('Content-Type', 'application/json')

  const raw = JSON.stringify({
    title: 'New Care Plan Suggestion',
    body: `${titleCase(
      patientName
    )} (${patientAge},${patientGender}) has a new Careplan from Command Center.`,
    keycloakId: doctorId,
    patientId: fhirId,
    data: {
      name: patientName,
      age: patientAge?.toString(),
      gender: patientGender,
      date: getLocalDateAndTime(),
      origin: 'ipd',
      critical: 'true',
      visitId: newVisitID,
    },
  })

  const requestOptions: RequestInit = {
    method: 'POST',
    headers: myHeaders,
    body: raw,
    redirect: 'follow',
  }

  fetch(
    `${import.meta.env.VITE_DMS_API_URL}${import.meta.env.VITE_DOCTOR_CAREPLAN_NOTIFICATION_ENDPOINT}`,
    requestOptions
  )
    .then((res) => {
      if (!res?.ok) throw new Error('Failed to care plan notification')
      return res?.json()
    })
    .then((result) => {
      if (!result?.success) {
        console.error(messages?.careplanNotificationFailure, result?.message)
      }
    })
    .catch((error) => console.error(error))
}

export const getAllProrithmDevices = async (
  hasCategoryType?: boolean,
  type?: string
): Promise<{
  success: boolean
  data: DevicesList[]
  message: string
}> => {
  const endpoint = hasCategoryType
    ? `${endpoints?.allProrithmDevices}?categoryType=${type}&deviceType=PRORITHM`
    : `${endpoints?.allProrithmDevices}?deviceType=PRORITHM`

  try {
    const myHeaders = new Headers()
    myHeaders.append('Authorization', `Bearer ${KeycloakService.getToken()}`)
    myHeaders.append('Content-Type', 'application/json')

    const requestOptions: RequestInit = {
      method: 'GET',
      headers: myHeaders,
      redirect: 'follow',
    }

    const response = await fetch(
      `${import.meta.env.VITE_DMS_API_URL}${endpoint}`,
      requestOptions
    )

    if (!response?.ok) {
      const errorData = await response?.json()
      const errorMessage = errorData?.message
      throw new Error(errorMessage)
    }

    const result = await response?.json()
    const content = result?.data?.content ?? []

    if (Array.isArray(content) && content?.length > 0) {
      return {
        success: true,
        data: content,
        message: result?.message || 'Success',
      }
    } else {
      return {
        success: false,
        data: [],
        message: 'Failed',
      }
    }
  } catch (error: any) {
    console.error(error)
    return {
      success: false,
      data: [],
      message: error?.message,
    }
  }
}

export const searchPatient = async (
  type: string,
  value: string,
  isIpdCategory: boolean
): Promise<{
  data: IpdSearchOptions[]
  message: string
  success: boolean
}> => {
  const token = KeycloakService.getToken()

  const myHeaders = new Headers()
  myHeaders.append('Authorization', `Bearer ${token}`)

  const requestOptions: RequestInit = {
    method: 'GET',
    headers: myHeaders,
    redirect: 'follow',
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_DMS_API_URL}${endpoints?.searchPatient}type=${type}&value=${value}&isIpdCategory=${isIpdCategory}`,
      requestOptions
    )
    if (!response?.ok) throw new Error(messages?.searchPatientError)
    const data = await response?.json()
    return data
  } catch (error) {
    console.error(error)
    return { data: [], message: (error as Error).message, success: false }
  }
}

export const fetchMedicationAdministrationWithId = (
  id: number,
  handleMedicationAdministrationSlots: (data: any) => void
): void => {
  const payloadForDocId = {
    intent: intent?.getAdministerMedicineById,
    state: {
      medication_id: id,
    },
  }
  fetch(createApiUrl(IPD_PROJECT_ID), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${KeycloakService.getToken()}`,
      'Content-Type': 'application/json',
      version: 'WORKING',
    },
    body: JSON.stringify(payloadForDocId),
  })
    .then((res) => {
      if (!res?.ok)
        throw new Error(messages?.fetchMedicationAdministrationFailure)
      return res?.json()
    })
    .then((data) => {
      handleMedicationAdministrationSlots(data)
    })
    .catch((err) => console.error(err))
}

export const editMedicationById = async (
  id: string,
  reason: string
): Promise<{
  success: any
  message: any
}> => {
  const payload = {
    intent: intent?.editMedicationById,
    state: {
      id: id,
      status: constants?.inactive,
      discontinuedOn: dayjs().format('YYYY-MM-DDTHH:mm:ssZ'),
      reason: reason,
    },
  }
  try {
    const response = await fetch(createApiUrl(IPD_PROJECT_ID), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${KeycloakService.getToken()}`,
        'Content-Type': 'application/json',
        version: 'WORKING',
      },
      body: JSON.stringify(payload),
    })

    if (!response?.ok) {
      throw new Error(messages?.failedToDiscontinueMedication)
    }

    const data = await response.json()
    return { success: data?.success, message: data?.message }
  } catch (err: any) {
    console.error(err)
    return { success: false, message: err?.message }
  }
}

export const fetchAllVisitMedications = async (
  visitIds: number[]
): Promise<VisitMedication[]> => {
  const payloadForDocId = {
    intent: intent?.getallVisitmedication,
    state: {
      visitIds: visitIds,
    },
  }

  try {
    const response = await fetch(createApiUrl(OPD_PROJECT_ID), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${KeycloakService.getToken()}`,
        'Content-Type': 'application/json',
        version: 'WORKING',
      },
      body: JSON.stringify(payloadForDocId),
    })

    if (!response?.ok) {
      throw new Error(messages?.failedToFetchMedications)
    }

    const data = await response?.json()
    return data?.medications || []
  } catch (err) {
    console.error(err)
    return []
  }
}
